import React, { useState, useEffect } from 'react';

import io from 'socket.io-client';

import { useClient } from '../Client/Client';

import { ChatClientContextType, SocketType } from './types';
import { ConversationMessageType } from '../../features/Messaging/types';

const defaultSocket: SocketType = {
  id: '',
  on: (event: string, fn: Function) => {},
  off: (event: string) => {},
  emit: (event: string, ...args: any[]) => {},
  disconnect: () => {},
};

const defaultContext: ChatClientContextType = {
  chatSocket: defaultSocket,
  roomSocket: defaultSocket,
  chatConnect: () => () => {},
  chatDisconnect: () => {},
  listenToUpdateConversation: () => () => {},
  subscribeToConversation: () => {},
  unsubscribeToConversation: () => {},
  emitMessage: () => {},
  listenToNewConversationMessage: () => () => {},
  listenToUnreadConversationsCount: () => () => {},
  listenToConversationMarkedAsRead: () => () => {},
  emitDeleteMessage: () => {},
  listenToDeleteMessage: () => {},
  listenToEditedMessage: () => {},
};

type makeClientProviderProps = {
  children: React.ReactNode;
};

export const ChatClientContext =
  React.createContext<ChatClientContextType>(defaultContext);

export const makeClientProvider =
  (): React.FC<makeClientProviderProps> =>
  ({ children }) => {
    const [chatSocket, setChatSocket] = useState<SocketType>(defaultSocket);
    const [roomSocket, setRoomSocket] = useState<SocketType>(defaultSocket);
    const { status } = useClient();

    const api: Omit<ChatClientContextType, 'chatSocket' | 'roomSocket'> =
      React.useMemo(() => {
        const chatConnect = (userId: string) => {
          if (!chatSocket?.id) {
            const chatSocket = io(
              `${process.env.REACT_APP_MR_CLIENT_CHAT_URL}/rooms`,
              	{}
		//{ transports: ['websocket'] }
            );

            chatSocket.emit('online', userId);
            setChatSocket(chatSocket);
          }

          if (!roomSocket?.id) {
            const roomSocket = io(
              `${process.env.REACT_APP_MR_CLIENT_CHAT_URL}/chat-room`,
              	{}
		//{ transports: ['websocket'] }
            );

            setRoomSocket(roomSocket);
          }
        };

        const chatDisconnect = () => {
          chatSocket && chatSocket.disconnect();
          roomSocket && roomSocket.disconnect();
        };

        const listenToUpdateConversation = (callback: any) => {
          chatSocket.off('updateConversation');
          chatSocket.on('updateConversation', callback);
        };

        const listenToUnreadConversationsCount = (callback: any) => {
          chatSocket.on('conversationsUnread', callback);
        };

        const subscribeToConversation = (
          conversationId: string | undefined,
          chatUserId: string | undefined
        ) => {
          if (conversationId && chatUserId) {
            roomSocket.emit('subscribe', conversationId, chatUserId);
            roomSocket.emit(
              'markAllMessagesAsRead',
              conversationId,
              chatUserId
            );
          }
        };

        const unsubscribeToConversation = (
          conversationId: string | undefined,
          chatUserId: string | undefined
        ) => {
          if (conversationId && chatUserId) {
            roomSocket.emit('unsubscribe', conversationId, chatUserId);
          }
        };

        const emitMessage = (message: ConversationMessageType | undefined) => {
          roomSocket.emit('publish', message);
        };

        const listenToNewConversationMessage = (callback: any) => {
          roomSocket.on('publish', callback);
        };

        const listenToConversationMarkedAsRead = (callback: any) => {
          chatSocket.off('conversationMarkedAsRead');
          chatSocket.on('conversationMarkedAsRead', callback);
        };

         const emitDeleteMessage = (id: string, users: string[]) => {
           roomSocket.emit('deleteMessage', id, users);
         };

         const listenToDeleteMessage = (callback: any) => { 
           roomSocket.on('deleteMessage', callback);
         };

         const listenToEditedMessage = (callback: any) => {
           roomSocket.on('editMessage', callback);
         };

        return {
          chatConnect,
          chatDisconnect,
          listenToUpdateConversation,
          subscribeToConversation,
          unsubscribeToConversation,
          emitMessage,
          listenToNewConversationMessage,
          listenToUnreadConversationsCount,
          listenToConversationMarkedAsRead,
          emitDeleteMessage,
          listenToDeleteMessage,
          listenToEditedMessage,
        };
      }, [chatSocket, roomSocket]);

    useEffect(() => {
      status === 'anonymous' && api.chatDisconnect();
    }, [status, api]);

    return (
      <ChatClientContext.Provider value={{ ...api, chatSocket, roomSocket }}>
        {children}
      </ChatClientContext.Provider>
    );
  };

export const ChatClientProvider = makeClientProvider();
ChatClientProvider.displayName = 'ChatClientProvider';

export const useChatClient = () => React.useContext(ChatClientContext);
